import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  MessageSquare,
  ClipboardCheck,
  ListChecks,
  BookOpen,
  SearchCheck,
  FileText,
  Table2,
  Users,
  Building2,
  FileCog,
  FolderCog,
  Settings,
  ScrollText,
  UserPlus,
  Bug,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { Avatar } from '@/components/ui/Avatar';
import { useAuthStore } from '@/store/authStore';
import { usePlatformViewStore } from '@/store/platformViewStore';
import { getMyPendingCount } from '@/services/supabase/decisionApprovals';
import type { FeatureFlags } from '@/types';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  badgeCount?: number;
  /** Badge pill color per 01_DESIGN_SYSTEM.md Step 7 (red for decisions, amber for approvals). */
  badgeColor?: 'red' | 'amber';
  /** organisation.feature_flags key gating this item (09_ADMIN.md "Feature Flag Enforcement") — hidden only when explicitly `false`; null/missing/no key means always shown. */
  featureFlag?: keyof FeatureFlags;
}

const MAIN_NAV: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/chat', label: 'Ask DECIXA', icon: MessageSquare, featureFlag: 'ai_chat' },
  { to: '/decisions', label: 'Decision Check', icon: ClipboardCheck, badgeColor: 'red', featureFlag: 'decision_check' },
  { to: '/approvals', label: 'My Approvals', icon: ListChecks, badgeColor: 'amber' },
  { to: '/policies', label: 'Policies', icon: BookOpen, featureFlag: 'policy_navigator' },
  { to: '/compliance', label: 'Compliance Review', icon: SearchCheck, featureFlag: 'compliance_review' },
  { to: '/templates', label: 'Templates', icon: FileText, featureFlag: 'templates' },
  { to: '/doa', label: 'Approvals Matrix', icon: Table2 },
];

const ADMIN_NAV: NavItem[] = [
  { to: '/admin/users', label: 'Users', icon: Users },
  { to: '/admin/departments', label: 'Departments', icon: Building2 },
  { to: '/admin/policies', label: 'Policies', icon: FileCog },
  { to: '/admin/templates', label: 'Templates', icon: FileText },
  { to: '/admin/documents', label: 'Documents', icon: FolderCog },
  { to: '/admin/access-requests', label: 'Access Requests', icon: UserPlus },
  { to: '/admin/settings', label: 'Settings', icon: Settings },
  { to: '/admin/audit', label: 'Audit Log', icon: ScrollText },
  { to: '/admin/errors', label: 'Error Log', icon: Bug },
];

export interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  /** Rendered as an overlay drawer on small viewports; controlled by MobileHeader. */
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

const BADGE_CLASSES: Record<NonNullable<NavItem['badgeColor']>, string> = {
  red: 'bg-danger text-white',
  amber: 'bg-warning text-white',
};

function NavList({ items, collapsed }: { items: NavItem[]; collapsed: boolean }) {
  return (
    <ul className="space-y-0.5">
      {items.map((item) => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            className={({ isActive }) =>
              cn(
                'mx-2 flex items-center gap-3 rounded px-3 py-2 text-sm font-medium transition-colors',
                'text-text-secondary hover:bg-surface-secondary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                isActive && 'bg-primary-light text-primary font-medium'
              )
            }
          >
            <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
            {!collapsed && <span className="truncate">{item.label}</span>}
            {!collapsed && item.badgeCount ? (
              <span className={cn('ml-auto rounded-full px-2 py-0.5 text-xs font-semibold', BADGE_CLASSES[item.badgeColor ?? 'red'])}>
                {item.badgeCount}
              </span>
            ) : null}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

export function Sidebar({ collapsed, onToggle, mobileOpen = false, onMobileClose }: SidebarProps) {
  const { member, organisation, platformAdmin } = useAuthStore();
  const { viewingOrgId, viewingOrgName } = usePlatformViewStore();
  // A platform admin visiting an org has no member.role — show the Admin
  // section for them too (full read-only parity), not just real admins.
  const isAdmin = member?.role === 'admin' || member?.role === 'owner' || !!(platformAdmin && viewingOrgId);

  const orgName = organisation?.name ?? viewingOrgName;
  const userName = member?.display_name || member?.work_email || platformAdmin?.display_name || platformAdmin?.email;

  // Same query key AppShell's useApprovalsRealtime() invalidates, so the
  // realtime subscription refreshes this badge without a page reload (T037).
  const pendingApprovalsQuery = useQuery({
    queryKey: ['dashboard', 'pendingCount'],
    queryFn: getMyPendingCount,
    enabled: !!member,
  });
  const flags = organisation?.feature_flags;
  const mainNav = MAIN_NAV.filter((item) => !item.featureFlag || flags?.[item.featureFlag] !== false).map((item) =>
    item.to === '/approvals' ? { ...item, badgeCount: pendingApprovalsQuery.data } : item
  );

  const content = (
    <>
      <div className="flex h-14 items-center gap-2 border-b border-border px-4">
        <div className="flex h-8 w-8 items-center justify-center rounded bg-primary text-sm font-bold text-white">D</div>
        {!collapsed && <span className="text-lg font-semibold tracking-tight text-text-primary">DECIXA</span>}
      </div>

      {orgName && (
        <div className={cn('flex items-center gap-2 border-b border-border bg-surface-secondary px-4 py-3', collapsed && 'justify-center px-2')}>
          <div className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded bg-primary-mid text-xs font-semibold text-primary">
            {orgName.charAt(0).toUpperCase()}
          </div>
          {!collapsed && <span className="truncate text-sm font-medium text-text-primary">{orgName}</span>}
        </div>
      )}

      <nav className="flex-1 overflow-y-auto py-2">
        <NavList items={mainNav} collapsed={collapsed} />

        {isAdmin && (
          <div className="mt-2">
            {!collapsed && <p className="px-3 pb-1 pt-4 text-xs font-semibold uppercase tracking-widest text-text-muted">Admin</p>}
            <NavList items={ADMIN_NAV} collapsed={collapsed} />
          </div>
        )}
      </nav>

      {userName && (
        <div className={cn('flex items-center gap-2 border-t border-border bg-surface-secondary p-3', collapsed && 'justify-center')}>
          <Avatar name={userName} size="xs" />
          {!collapsed && <span className="truncate text-sm font-medium text-text-primary">{userName}</span>}
        </div>
      )}

      <button
        type="button"
        onClick={onToggle}
        className="hidden border-t border-border px-4 py-3 text-left text-xs font-medium text-text-secondary hover:text-text-primary md:block"
      >
        {collapsed ? '»' : '« Collapse'}
      </button>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-30 hidden flex-col border-r border-border bg-surface transition-all duration-300 print:hidden md:flex',
          collapsed ? 'w-20' : 'w-64'
        )}
      >
        {content}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={onMobileClose} aria-hidden="true" />
          <aside className="absolute inset-y-0 left-0 flex w-72 flex-col border-r border-border bg-surface">{content}</aside>
        </div>
      )}
    </>
  );
}
